import React, { useState, useRef, useEffect, useCallback } from 'react';
import Modal from './Modal';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DateRangePreset =
  | 'current_month'
  | 'last_month'
  | 'last_two_months'
  | 'last_three_months'
  | 'month'
  | 'on_range';

export interface DateRangeValue {
  preset: DateRangePreset;
  from: Date;
  to: Date;
  label: string;
}

interface DateRangeFilterProps {
  value?: DateRangeValue | null;
  onChange: (value: DateRangeValue) => void;
  disabled?: boolean;
  /** Earliest selectable month (e.g. the selected org's creation month). No lower bound when omitted. */
  minMonth?: Date | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function shiftMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
function fmtShortDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}
function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computePreset(
  preset: Exclude<DateRangePreset, 'month' | 'on_range'>,
  minMonth?: Date | null
): { from: Date; to: Date; label: string } {
  const now = new Date();

  const dataRangeOptions = () => {
    switch (preset) {
      case 'current_month':
        return { from: startOfMonth(now), to: endOfMonth(now), label: 'Current month' };
      case 'last_month': {
        const m = shiftMonths(now, -1);
        return { from: startOfMonth(m), to: endOfMonth(m), label: 'Last month' };
      }
      case 'last_two_months': {
        // "Last 2 months" includes the current month, so the start is only
        // 1 month back (current + previous = 2 months total), not 2.
        const m = shiftMonths(now, -1);
        return { from: startOfMonth(m), to: endOfMonth(now), label: 'Last 2 months' };
      }
      case 'last_three_months': {
        // Same logic: current + 2 previous months = 3 months total.
        const m = shiftMonths(now, -2);
        return { from: startOfMonth(m), to: endOfMonth(now), label: 'Last 3 months' };
      }
    }
  };

  const predefinedOptions = dataRangeOptions();

  // Clamp the lower bound so no months before minMonth (e.g. the selected
  // org's creation month) are ever included in the resulting range.
  if (minMonth) {
    const earliestStart = startOfMonth(minMonth);
    if (predefinedOptions.from < earliestStart) {
      return { ...predefinedOptions, from: earliestStart };
    }
  }
  return predefinedOptions;
}

/** Default date range shown on initial load / whenever the scope (e.g. selected org) changes. */
export function getDefaultDateRange(minMonth?: Date | null): DateRangeValue {
  return { preset: 'current_month', ...computePreset('current_month', minMonth) };
}

const MAX_MONTH_COUNT = 6;
const MAX_SELECTABLE_MONTHS = 600;

function getSelectableMonths(minMonth?: Date | null): Date[] {
  const now = new Date();

  if (!minMonth) {
    return Array.from({ length: MAX_MONTH_COUNT }, (_, i) => shiftMonths(now, -i));
  }

  const earliestStart = startOfMonth(minMonth);
  const months: Date[] = [];
  let cursor = startOfMonth(now);
  // Guard against a pathological/incorrect minMonth far in the past.
  while (cursor >= earliestStart && months.length < MAX_SELECTABLE_MONTHS) {
    months.push(cursor);
    cursor = shiftMonths(cursor, -1);
  }
  return months;
}

const PRESET_OPTIONS: { preset: DateRangePreset; label: string }[] = [
  { preset: 'current_month', label: 'Current month' },
  { preset: 'last_month', label: 'Last month' },
  { preset: 'last_two_months', label: 'Last two months' },
  { preset: 'last_three_months', label: 'Last three months' },
  { preset: 'month', label: 'Month…' },
  { preset: 'on_range', label: 'On range' },
];

// ── Component ─────────────────────────────────────────────────────────────────

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  disabled = false,
  minMonth = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const selectableMonths = getSelectableMonths(minMonth);
  const minRangeInputDate = minMonth ? toInputDate(startOfMonth(minMonth)) : undefined;

  // Close dropdown on outside click
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const triggerLabel = value
    ? `${fmtShortDate(value.from)} – ${fmtShortDate(value.to)}`
    : 'Select range';

  function handlePresetClick(preset: DateRangePreset) {
    if (preset === 'month') {
      setIsOpen(false);
      setShowMonthModal(true);
      return;
    }
    if (preset === 'on_range') {
      // stay open, show range inputs (handled in render)
      return;
    }
    const r = computePreset(preset as Exclude<DateRangePreset, 'month' | 'on_range'>, minMonth);
    onChange({ preset, ...r });
    setIsOpen(false);
  }

  function handleMonthSelect(month: Date) {
    const from = startOfMonth(month);
    const to = endOfMonth(month);
    onChange({ preset: 'month', from, to, label: fmtMonthYear(month) });
    setShowMonthModal(false);
  }

  function handleRangeApply() {
    if (!rangeFrom || !rangeTo) return;
    const from = new Date(rangeFrom + 'T00:00:00');
    const to = new Date(rangeTo + 'T23:59:59');
    if (from > to) return;
    onChange({
      preset: 'on_range',
      from,
      to,
      label: `${fmtShortDate(from)} – ${fmtShortDate(to)}`,
    });
    setIsOpen(false);
  }

  const activePreset = value?.preset;

  return (
    <>
      <div ref={containerRef} className="drf-root">
        {/* Trigger */}
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => !disabled && setIsOpen((o) => !o)}
          className={`drf-trigger${isOpen ? ' drf-trigger--open' : ''}${
            disabled ? ' drf-trigger--disabled' : ''
          }`}
        >
          <span className="drf-trigger-label">Date Range:</span>
          <span className="drf-trigger-value">{triggerLabel}</span>
          <svg
            className={`drf-chevron${isOpen ? ' drf-chevron--up' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M2 4l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="drf-dropdown" role="listbox">
            {PRESET_OPTIONS.map(({ preset, label }) => (
              <React.Fragment key={preset}>
                <button
                  type="button"
                  role="option"
                  aria-selected={activePreset === preset}
                  className={`drf-option${activePreset === preset ? ' drf-option--active' : ''}`}
                  onClick={() => handlePresetClick(preset)}
                >
                  {label}
                </button>

                {/* Inline range inputs — shown when "On range" is the last-clicked preset  */}
                {preset === 'on_range' &&
                  activePreset !== 'on_range' &&
                  isOpen &&
                  /* Only expand when user explicitly clicked it; tracked via no activePreset match yet */
                  null}
              </React.Fragment>
            ))}

            {/* Range sub-panel — always visible at bottom of dropdown for easy access */}
            <div className="drf-range-panel">
              <label className="drf-range-label">From</label>
              <input
                type="date"
                className="drf-range-input"
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
                min={minRangeInputDate}
                max={rangeTo || toInputDate(new Date())}
              />
              <label className="drf-range-label">To</label>
              <input
                type="date"
                className="drf-range-input"
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
                min={rangeFrom || minRangeInputDate}
                max={toInputDate(new Date())}
              />
              <button
                type="button"
                className="drf-range-apply"
                disabled={!rangeFrom || !rangeTo}
                onClick={handleRangeApply}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Month Picker Modal */}
      <Modal
        isOpen={showMonthModal}
        onClose={() => setShowMonthModal(false)}
        title="Select Month"
        size="sm"
      >
        <div className="drf-month-list">
          {selectableMonths.map((month) => {
            const isActive =
              activePreset === 'month' &&
              value &&
              value.from.getMonth() === month.getMonth() &&
              value.from.getFullYear() === month.getFullYear();
            return (
              <button
                key={month.toISOString()}
                type="button"
                className={`drf-month-item${isActive ? ' drf-month-item--active' : ''}`}
                onClick={() => handleMonthSelect(month)}
              >
                {fmtMonthYear(month)}
              </button>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default DateRangeFilter;
